import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  type ChannelMarkupConditionApplyType,
  type ChannelMarkupConditionType,
  type ChannelMarkupEvaluationContext,
  type ChannelMarkupV2ClientInit,
  type ClientInitConfig,
  channelMarkupConditionConfig,
  evaluateChannelMarkupRound,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';
import type { components, operations } from '@luxuryescapes/contract-svc-promo/generated';

import { type ChannelMarkupV2Internal, createOrUpdateChannelMarkupV2 } from '~/services/PromoService';

import { channelMarkupV2QueryKeys } from './useGetChannelMarkupV2ById';

type Condition = {
  id: string;
  condition_type: ChannelMarkupConditionType;
  condition_apply_type: string;
  condition_value: string;
  specificMarkupIds: Array<string>;
};

type Markup = {
  id: string;
  markup_type: 'fixed' | 'percentage';
  markup_value: string;
  lux_plus_markup_value: string;
  channelMarkupCTAText: string;
  currency: components['schemas']['Promo Currencies'] | '';
};

type TesterValues = {
  context: ChannelMarkupEvaluationContext;
};

type ChannelMarkupV2Payload = operations['createChannelMarkupV2']['requestBody']['content']['application/json'];
type CreateOrUpdateChannelMarkupV2Response =
  | operations['createChannelMarkupV2']['responses']['201']['content']['application/json']
  | operations['updateChannelMarkupV2']['responses']['200']['content']['application/json'];

type TesterScenarioResult = {
  context: ChannelMarkupEvaluationContext;
  region: string;
  currency: string;
  status: ReturnType<typeof evaluateChannelMarkupRound>['status'];
  evaluation: ReturnType<typeof evaluateChannelMarkupRound>;
  conditionEvaluations: Array<{
    id: string;
    conditionType: string;
    conditionValue: string;
    passed: boolean;
    contextValue: string;
  }>;
  presentation: {
    channelMarkupCallToActionText: string | null;
    matchCount: number;
  } | null;
};

type ApplyParams = {
  isEditMode: boolean;
  createPayload: ChannelMarkupV2Payload;
  updatePayload: operations['updateChannelMarkupV2']['requestBody']['content']['application/json'];
};

type ApplyResult = {
  response: CreateOrUpdateChannelMarkupV2Response;
  channelMarkup: ChannelMarkupV2Internal | null;
  warnings: Array<string>;
};

const extractChannelMarkupFromResponse = (
  response: CreateOrUpdateChannelMarkupV2Response,
): ChannelMarkupV2Internal | null => {
  const result = response.result;
  return result ?? null;
};

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const trimOrFallback = (value: string | undefined, fallback: string | null = ''): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

const toNullableNumber = (value: string | undefined): number | null => {
  if (value === undefined || value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatContextValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ');
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }

  return '';
};

const buildConditionEvaluations = (evaluation: ReturnType<typeof evaluateChannelMarkupRound>) => {
  const evaluations = evaluation.state.candidates.flatMap((candidate) => candidate.evaluations);

  return evaluations.map((condition, index) => ({
    id: `${condition.conditionId}-${index}`,
    conditionType: condition.condition_type,
    conditionValue: condition.condition_value,
    passed: condition.status === 'match',
    contextValue: formatContextValue(condition.context.contextValue),
  }));
};

const buildRulesPayload = (
  conditions: Array<Condition>,
  markups: Array<Markup>,
): ChannelMarkupV2ClientInit['rules'] => ({
  conditions: conditions.map((condition) => {
    const conditionConfig =
      channelMarkupConditionConfig.find((config) => config.key === condition.condition_type) ?? null;
    const fallbackApplyType = conditionConfig?.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE';
    const conditionType = conditionConfig?.key ?? channelMarkupConditionConfig[0]?.key;
    const conditionApplyType = isChannelMarkupConditionApplyType(condition.condition_apply_type)
      ? condition.condition_apply_type
      : fallbackApplyType;

    if (!conditionType) {
      throw new Error('Missing channel markup condition configuration');
    }

    return {
      conditionId: condition.id,
      specificMarkupIds: condition.specificMarkupIds.length > 0 ? condition.specificMarkupIds : null,
      conditionType,
      conditionApplyType,
      conditionValue: condition.condition_value,
    };
  }),
  markups: markups.map((markup) => ({
    markupId: markup.id,
    markupType: markup.markup_type,
    markupValue: toNumberOrUndefined(markup.markup_value),
    luxPlusMarkupValue: toNumberOrUndefined(markup.lux_plus_markup_value),
    channelMarkupCTAText: markup.channelMarkupCTAText || null,
    ...(markup.currency ? { currency: markup.currency } : {}),
  })),
});

export const normalizeResponseWarnings = (response: unknown): Array<string> => {
  if (!isResponseWithWarnings(response)) {
    return [];
  }

  const warnings = response.result?.warnings ?? response.warnings ?? [];
  return warnings
    .map((warning) => (typeof warning === 'string' ? warning : warning.message))
    .filter((warning): warning is string => Boolean(warning));
};

function isResponseWithWarnings(value: unknown): value is {
  warnings?: Array<{ message?: string } | string>;
  result?: { warnings?: Array<{ message?: string } | string> };
} {
  return typeof value === 'object' && value !== null && ('warnings' in value || 'result' in value);
}

export function useChannelMarkupV2() {
  const queryClient = useQueryClient();

  const evaluateChannelMarkup = useCallback(
    ({
      channelMarkupId,
      conditions,
      markups,
      testerValues,
    }: {
      channelMarkupId: string;
      conditions: Array<Condition>;
      markups: Array<Markup>;
      testerValues: TesterValues;
    }): TesterScenarioResult => {
      const { context: testerContext } = testerValues;
      const region = trimOrFallback(testerContext.region ?? undefined, 'AU') ?? 'AU';
      const currency = testerContext.currency?.trim() || 'AUD';
      const channelMarkupConfig: ClientInitConfig['channelMarkupConfig'] = {
        channelMarkupListV1: [],
        channelMarkupListV2: [
          {
            version: 'v2',
            channelMarkupId,
            rules: buildRulesPayload(conditions, markups),
          },
        ],
        globalOfferIdBlockList: [],
      };
      const context: ChannelMarkupEvaluationContext = {
        region,
        currency,
        subscriberTier: trimOrFallback(testerContext.subscriberTier ?? undefined, null),
        roles: testerContext.roles ?? [],
        offer: testerContext.offer
          ? {
              version: 'cmOfferV1',
              total: toNullableNumber(String(testerContext.offer.total ?? '')) ?? 0,
              luxPlusPrice: toNullableNumber(String(testerContext.offer.luxPlusPrice ?? '')),
              offerId: trimOrFallback(testerContext.offer.offerId ?? undefined, 'test-offer') ?? 'test-offer',
              loyaltyOfferType:
                trimOrFallback(testerContext.offer.loyaltyOfferType ?? undefined, 'HOTEL_FLASH') ?? 'HOTEL_FLASH',
            }
          : {
              version: 'cmOfferV1',
              total: 0,
              luxPlusPrice: null,
              offerId: 'test-offer',
              loyaltyOfferType: 'HOTEL_FLASH',
            },
      };
      const evaluation = evaluateChannelMarkupRound({
        channelMarkupConfig,
        context,
      });

      return {
        context,
        region,
        currency,
        status: evaluation.status,
        evaluation,
        conditionEvaluations: buildConditionEvaluations(evaluation),
        presentation:
          evaluation.status === 'matched' && evaluation.matches.length > 0
            ? {
                channelMarkupCallToActionText: evaluation.matches[0].markup.channelMarkupCTAText ?? null,
                matchCount: evaluation.matches.length,
              }
            : null,
      };
    },
    [],
  );

  const applyChannelMarkup = useCallback(
    async ({ isEditMode, createPayload, updatePayload }: ApplyParams): Promise<ApplyResult> => {
      const response = await createOrUpdateChannelMarkupV2(isEditMode ? updatePayload : createPayload, isEditMode);
      // Bust cached channel markup detail(s) so a re-open/refresh reflects the saved change immediately.
      await queryClient.invalidateQueries({ queryKey: channelMarkupV2QueryKeys.all });
      return {
        response,
        channelMarkup: extractChannelMarkupFromResponse(response),
        warnings: normalizeResponseWarnings(response),
      };
    },
    [queryClient],
  );

  return {
    evaluateChannelMarkup,
    applyChannelMarkup,
  };
}

function isChannelMarkupConditionApplyType(value: string): value is ChannelMarkupConditionApplyType {
  return channelMarkupConditionConfig.some((config) =>
    config.conditionApplyTypes?.some((conditionApplyType) => conditionApplyType === value),
  );
}
